import { PlotFeatureNotFoundError, } from "@plotlibre/core";
/**
 * Engine-independent transient multi-selection state.
 *
 * Selection membership is reconciled against PlotStore removals, but selection
 * itself never mutates PlotFeature state or enters PlotJSON.
 */
export class SelectionController {
    #store;
    #listeners = new Set();
    #unsubscribeStore;
    #selectedIds = [];
    #revision = 0;
    #storeReconciliationPauseDepth = 0;
    constructor(store) {
        this.#store = store;
        this.#unsubscribeStore = store.subscribe(() => {
            this.#reconcileStore();
        });
    }
    snapshot() {
        return createSnapshot(this.#selectedIds, this.#revision);
    }
    get selectedIds() {
        return [...this.#selectedIds];
    }
    get primaryId() {
        return this.#selectedIds.at(-1);
    }
    get revision() {
        return this.#revision;
    }
    get size() {
        return this.#selectedIds.length;
    }
    has(id) {
        return this.#selectedIds.includes(id);
    }
    /**
     * Applies an adapter-normalized click/selection intent without inspecting any
     * browser or map-engine modifier keys.
     */
    applyIntent(id, intent) {
        if (id === undefined) {
            return intent === "replace" ? this.clear() : this.snapshot();
        }
        this.#assertExists(id);
        switch (intent) {
            case "replace":
                return this.has(id) ? this.makePrimary(id) : this.replace([id]);
            case "add":
                return this.add([id]);
            case "subtract":
                return this.has(id) ? this.subtract([id]) : this.snapshot();
            case "toggle":
                return this.toggle(id);
        }
    }
    /**
     * Applies one ordered multi-id box/lasso intent and emits at most one change.
     * The adapter is responsible for supplying ids in deterministic Store order.
     */
    applyMany(ids, intent, reason) {
        const normalized = this.#normalizeExistingIds(ids);
        const currentSet = new Set(this.#selectedIds);
        switch (intent) {
            case "replace":
                return this.#commit(normalized, reason);
            case "add": {
                const additions = normalized.filter((id) => !currentSet.has(id));
                if (additions.length === 0)
                    return this.snapshot();
                return this.#commit([...this.#selectedIds, ...additions], reason);
            }
            case "subtract": {
                if (normalized.length === 0)
                    return this.snapshot();
                const removals = new Set(normalized);
                return this.#commit(this.#selectedIds.filter((id) => !removals.has(id)), reason);
            }
            case "toggle": {
                if (normalized.length === 0)
                    return this.snapshot();
                const toggles = new Set(normalized);
                const survivors = this.#selectedIds.filter((id) => !toggles.has(id));
                const additions = normalized.filter((id) => !currentSet.has(id));
                return this.#commit([...survivors, ...additions], reason);
            }
        }
    }
    replace(ids, primaryId, reason = "replace") {
        const normalized = this.#normalizeExistingIds(ids);
        const primary = primaryId ?? normalized.at(-1);
        if (primary !== undefined && !normalized.includes(primary)) {
            throw new RangeError(`Primary selection "${primary}" must belong to selectedIds.`);
        }
        const next = primary === undefined
            ? normalized
            : moveToEnd(normalized, primary);
        return this.#commit(next, reason);
    }
    add(ids) {
        const normalized = this.#normalizeExistingIds(ids);
        if (normalized.length === 0)
            return this.snapshot();
        const next = [...this.#selectedIds];
        for (const id of normalized) {
            if (!next.includes(id))
                next.push(id);
        }
        // Click-add preserves the merged 007A behavior: the final requested id
        // becomes primary even when it was already selected.
        return this.#commit(moveToEnd(next, normalized.at(-1)), "add");
    }
    subtract(ids) {
        const normalized = this.#normalizeExistingIds(ids);
        if (normalized.length === 0)
            return this.snapshot();
        const removals = new Set(normalized);
        return this.#commit(this.#selectedIds.filter((id) => !removals.has(id)), "subtract");
    }
    toggle(id) {
        this.#assertExists(id);
        if (this.has(id)) {
            return this.#commit(this.#selectedIds.filter((selectedId) => selectedId !== id), "toggle");
        }
        return this.#commit([...this.#selectedIds, id], "toggle");
    }
    makePrimary(id) {
        this.#assertExists(id);
        if (!this.has(id)) {
            throw new RangeError(`Cannot make unselected feature "${id}" the primary selection.`);
        }
        return this.#commit(moveToEnd(this.#selectedIds, id), "primary");
    }
    clear(reason = "clear") {
        return this.#commit([], reason);
    }
    /**
     * Runs one synchronous Store mutation without intermediate automatic
     * reconciliation. Selection-aware commands use this to emit exactly one
     * explicit history selection event after a successful atomic commit.
     */
    runWithoutStoreReconciliation(operation) {
        this.#storeReconciliationPauseDepth += 1;
        try {
            return operation();
        }
        finally {
            this.#storeReconciliationPauseDepth -= 1;
        }
    }
    /**
     * Restores semantic membership/primary state while issuing a fresh monotonic
     * interaction revision. Stored snapshot.revision is intentionally not reused.
     */
    restore(snapshot, reason) {
        return this.replace(snapshot.selectedIds, snapshot.primaryId, reason);
    }
    subscribe(listener) {
        this.#listeners.add(listener);
        return () => {
            this.#listeners.delete(listener);
        };
    }
    destroy() {
        this.#unsubscribeStore();
        this.#listeners.clear();
    }
    #normalizeExistingIds(ids) {
        const normalized = [];
        const seen = new Set();
        for (const id of ids) {
            this.#assertExists(id);
            if (!seen.has(id)) {
                seen.add(id);
                normalized.push(id);
            }
        }
        return normalized;
    }
    #assertExists(id) {
        if (!this.#store.has(id)) {
            throw new PlotFeatureNotFoundError(id);
        }
    }
    #reconcileStore() {
        if (this.#storeReconciliationPauseDepth > 0)
            return;
        const surviving = this.#selectedIds.filter((id) => this.#store.has(id));
        this.#commit(surviving, "store-reconcile");
    }
    #commit(nextIds, reason) {
        if (sameIds(this.#selectedIds, nextIds))
            return this.snapshot();
        const before = this.snapshot();
        this.#selectedIds = [...nextIds];
        this.#revision += 1;
        const after = this.snapshot();
        const change = Object.freeze({ before, after, reason });
        for (const listener of [...this.#listeners])
            listener(change);
        return after;
    }
}
function createSnapshot(selectedIds, revision) {
    const ids = Object.freeze([...selectedIds]);
    const primaryId = ids.at(-1);
    return Object.freeze({
        selectedIds: ids,
        ...(primaryId !== undefined ? { primaryId } : {}),
        revision,
    });
}
function moveToEnd(ids, id) {
    return [...ids.filter((candidate) => candidate !== id), id];
}
function sameIds(left, right) {
    return (left.length === right.length &&
        left.every((id, index) => id === right[index]));
}
//# sourceMappingURL=selection-controller.js.map